import React, { useState, useEffect } from 'react';
import { getApiUrl } from '@/config/api';

interface Team {
  id: number;
  name: string;
}

interface InviteUserModalProps {
  onClose: () => void;
  defaultRole?: 'athlete' | 'coach';
  onSuccess?: () => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ onClose, defaultRole, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'athlete' | 'coach'>(defaultRole || 'athlete');
  const [teamId, setTeamId] = useState<number | ''>('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchTeams = async () => {
      const token = localStorage.getItem('access');
      if (!token) {
        setTeamsLoading(false);
        return;
      }
      try {
        const res = await fetch(getApiUrl('/api/teams/teams/'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (res.ok) {
          const data = await res.json();
          const rawTeams = Array.isArray(data)
            ? data
            : (data.results || data.data || []);
          const teamList: Team[] = (Array.isArray(rawTeams) ? rawTeams : []).map((t: any) => ({
            id: t.id,
            name: t.name,
          }));
          setTeams(teamList);
          if (teamList.length === 1) {
            setTeamId(teamList[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch teams:', err);
      } finally {
        setTeamsLoading(false);
      }
    };
    fetchTeams();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!teamId) {
      setError('Please select a team');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access');
      const res = await fetch(getApiUrl('/api/teams/admin/invite-user/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email: email.trim(), role, team_id: teamId }),
      });

      if (!res.ok) {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const data = await res.json();
          throw new Error(data.error || data.detail || `Request failed (${res.status})`);
        }
        throw new Error(`Request failed (${res.status})`);
      }

      const data = await res.json();
      setSuccess(data.message || 'Done');
      onSuccess?.();
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cgm-modal-backdrop">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="cgm-card cgm-modal-panel p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <h3 className="text-2xl cgm-text-primary mb-2">Invite {defaultRole === 'coach' ? 'Coach' : defaultRole === 'athlete' ? 'Athlete' : 'User'}</h3>
            <p className="cgm-text-secondary text-sm">
              Send an email invitation to join your team
            </p>
          </div>

          {error && (
            <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="cgm-alert-success px-4 py-3 rounded-md mb-4 text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="invite-email"
                className="block text-sm font-medium cgm-text-secondary mb-1"
              >
                Email Address
              </label>
              <input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="user@example.com"
              />
            </div>

            <div>
              <label
                htmlFor="invite-role"
                className="block text-sm font-medium cgm-text-secondary mb-1"
              >
                Role
              </label>
              <select
                id="invite-role"
                value={role}
                onChange={(e) => setRole(e.target.value as 'athlete' | 'coach')}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="athlete">Athlete</option>
                <option value="coach">Coach</option>
              </select>
            </div>

            {teams.length > 1 && (
              <div>
                <label
                  htmlFor="invite-team"
                  className="block text-sm font-medium cgm-text-secondary mb-1"
                >
                  Team
                </label>
                <select
                  id="invite-team"
                  value={teamId}
                  onChange={(e) => setTeamId(Number(e.target.value))}
                  disabled={loading || teamsLoading}
                  className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select a team</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {teams.length === 1 && (
              <div>
                <label className="block text-sm font-medium cgm-text-secondary mb-1">
                  Team
                </label>
                <div className="cgm-input w-full px-3 py-2 opacity-70">
                  {teams[0].name}
                </div>
              </div>
            )}

            {teams.length === 0 && !teamsLoading && (
              <div className="cgm-alert-error px-4 py-3 rounded-md text-sm">
                No teams found. Please create a team first.
              </div>
            )}

            <button
              type="submit"
              disabled={loading || teamsLoading || teams.length === 0}
              className="cgm-button w-full mt-6 py-3 px-4 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                  Sending Invite...
                </>
              ) : (
                'Send Invitation'
              )}
            </button>
          </form>

          <div className="mt-6 space-y-4 text-center">
            <button
              onClick={onClose}
              className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteUserModal;
